import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './lib/appInsights';
import { AuthProvider, useAuth, userIsAdmin, userIsSupervisor, userIsReviewer, userIsEngineer, userIsManager, userIsAccountant } from './context/AuthContext';
import { OptionsProvider } from './context/OptionsContext';
import LoginPage          from './pages/LoginPage';
import EmployeePortal     from './components/employee/EmployeePortal';
import SupervisorView     from './components/supervisor/SupervisorView';
import AdminPortal        from './components/admin/AdminPortal';
import VacationPortal     from './vacation/VacationPortal';
import TimesheetPortal    from './timesheet/TimesheetPortal';
import AccountingPortal   from './accounting/AccountingPortal';
import DashboardPage      from './components/shared/DashboardPage';
import GuiasPage          from './guias/GuiasPage';

function getDefaultRoute(user) {
  if (userIsAdmin(user))       return '/admin';
  if (userIsAccountant(user))  return '/contabilidad';
  return '/dashboard';
}

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f8fafc', fontFamily: 'Inter, ui-sans-serif, sans-serif',
        color: '#1e293b', fontSize: 16,
      }}>
        Loading…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Check role access using roles array
  if (requiredRole) {
    const hasAccess = requiredRole === 'admin'
      ? userIsAdmin(user)
      : requiredRole === 'accountant'
        ? (userIsAccountant(user) || userIsReviewer(user))
        : requiredRole === 'reviewer'
          ? userIsReviewer(user)
          : requiredRole === 'supervisor'
            ? (userIsSupervisor(user) || userIsReviewer(user) || userIsEngineer(user) || userIsManager(user))
            : user.roles?.includes(requiredRole) || userIsAdmin(user);

    if (!hasAccess) {
      return <Navigate to={getDefaultRoute(user)} replace />;
    }
  }

  return children;
}

function AuthRequired({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={getDefaultRoute(user)} replace />;
  return <LoginPage />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user)   return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRoute(user)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginGuard />} />
      <Route path="/"      element={<RootRedirect />} />
      <Route
        path="/employee/*"
        element={
          <AuthRequired>
            <EmployeePortal />
          </AuthRequired>
        }
      />
      <Route
        path="/supervisor/*"
        element={
          <ProtectedRoute requiredRole="supervisor">
            <SupervisorView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vacaciones/*"
        element={
          <AuthRequired>
            <VacationPortal />
          </AuthRequired>
        }
      />
      <Route
        path="/horas/*"
        element={
          <AuthRequired>
            <TimesheetPortal />
          </AuthRequired>
        }
      />
      <Route
        path="/contabilidad/*"
        element={
          <ProtectedRoute requiredRole="accountant">
            <AccountingPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AuthRequired>
            <DashboardPage />
          </AuthRequired>
        }
      />
      <Route
        path="/guias"
        element={
          <AuthRequired>
            <GuiasPage />
          </AuthRequired>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ErrorFallback() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc', fontFamily: 'Inter, ui-sans-serif, sans-serif',
      color: '#1e293b', padding: 32, textAlign: 'center',
    }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Algo salió mal</h1>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
        Ha ocurrido un error inesperado. Por favor recargue la página.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#d97706', color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
        }}
      >
        Recargar página
      </button>
    </div>
  );
}

export default function App() {
  const content = (
    <AuthProvider>
      <OptionsProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </OptionsProvider>
    </AuthProvider>
  );

  if (!reactPlugin) return content;

  return (
    <AppInsightsErrorBoundary onError={ErrorFallback} appInsights={reactPlugin}>
      {content}
    </AppInsightsErrorBoundary>
  );
}
